/**
 * PerformanceTab — KPI strip with delta badges, fatigue signal, daily chart,
 * and persona delivery rendered for the active persona's review.
 */

import { useMemo, useState } from 'react';
import { AlertTriangle, Target, TrendingUp, Users } from 'lucide-react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { clsx } from 'clsx';
import type { CreativeSiblingSummary, CreativeWorkspaceData } from '../types';
import { SiblingComparisonTable } from '../SiblingComparisonTable';

interface Props {
  data: CreativeWorkspaceData;
  siblings: CreativeSiblingSummary[];
}

const FATIGUE_WARNING_FREQUENCY = 2.5;
const FATIGUE_CRITICAL_FREQUENCY = 3.5;

interface KPI {
  label: string;
  value: string;
  delta?: number | null;
  isHigher: boolean;
  formatDelta?: (d: number) => string;
}

export function PerformanceTab({ data, siblings }: Props) {
  const creative = data.creative as Record<string, unknown>;
  const performance =
    (creative.performance as { lifetime?: Record<string, number | null> } | undefined)?.lifetime ??
    {};
  const series = data.series;
  const delivery = data.delivery;

  const lifetime = {
    spend: numOrNull(performance.spend),
    roas: numOrNull(performance.roas),
    ctr: numOrNull(performance.ctr),
    cpm: numOrNull(performance.cpm),
    impressions: numOrNull(performance.impressions),
    frequency: numOrNull(performance.frequency),
  };

  // Prior 7-day window: derive from the daily series if we have ≥14 days,
  // else omit delta badges (per spec, omit gracefully).
  const prior7d = useMemo(() => {
    if (!series || series.length < 14) return null;
    const last7 = series.slice(-7);
    const prev7 = series.slice(-14, -7);
    const sum = (rows: typeof series, key: keyof (typeof series)[number]) =>
      rows.reduce((s, r) => s + (Number(r[key]) || 0), 0);
    const avg = (rows: typeof series, key: keyof (typeof series)[number]) =>
      rows.length === 0 ? 0 : sum(rows, key) / rows.length;
    return {
      spend: sum(prev7, 'spend'),
      roas: avg(prev7, 'roas'),
      ctr: avg(prev7, 'ctr'),
      impressions: sum(prev7, 'impressions'),
      // Spend in last 7 days (for headline delta)
      lastSpend: sum(last7, 'spend'),
      lastRoas: avg(last7, 'roas'),
      lastCtr: avg(last7, 'ctr'),
    };
  }, [series]);

  const cards: KPI[] = [
    {
      label: 'Spend',
      value: lifetime.spend != null ? `$${lifetime.spend.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '—',
      delta: prior7d ? prior7d.lastSpend - prior7d.spend : null,
      isHigher: true,
      formatDelta: (d) => `${d >= 0 ? '+' : '−'}$${Math.abs(d).toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
    },
    {
      label: 'ROAS',
      value: lifetime.roas != null ? `${lifetime.roas.toFixed(2)}×` : '—',
      delta: prior7d && prior7d.roas !== 0 ? prior7d.lastRoas - prior7d.roas : null,
      isHigher: true,
      formatDelta: (d) => `${d >= 0 ? '+' : ''}${d.toFixed(2)}×`,
    },
    {
      label: 'CTR',
      value: lifetime.ctr != null ? `${(lifetime.ctr * 100).toFixed(2)}%` : '—',
      delta: prior7d && prior7d.ctr !== 0 ? (prior7d.lastCtr - prior7d.ctr) * 100 : null,
      isHigher: true,
      formatDelta: (d) => `${d >= 0 ? '+' : ''}${d.toFixed(2)}pp`,
    },
    {
      label: 'CPM',
      value: lifetime.cpm != null ? `$${lifetime.cpm.toFixed(2)}` : '—',
      isHigher: false,
    },
    {
      label: 'Frequency',
      value: lifetime.frequency != null ? lifetime.frequency.toFixed(2) : '—',
      isHigher: false,
    },
  ];

  const hasPerformance = lifetime.spend != null || (series?.length ?? 0) > 0;

  return (
    <div className="p-5 space-y-6">
      <div>
        <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-2">
          Lifetime Performance
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-2">
          {cards.map((card) => (
            <KPICard key={card.label} card={card} />
          ))}
        </div>
      </div>

      {lifetime.frequency != null && (
        <FatigueSignal frequency={lifetime.frequency} />
      )}

      {series && series.length > 0 && <DailyPerformanceChart series={series} />}

      {delivery && (delivery.personas?.length ?? 0) > 0 && (
        <PersonaDeliveryTable delivery={delivery} />
      )}

      <SiblingComparisonTable
        siblings={siblings}
        currentCreativeId={(creative.creative_id as string) ?? ''}
      />

      {!hasPerformance && <GeneratedCreativePerformancePlaceholder />}
    </div>
  );
}

function numOrNull(v: unknown): number | null {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function KPICard({ card }: { card: KPI }) {
  const delta = card.delta;
  let deltaBadge: React.ReactNode = null;
  if (delta != null && Math.abs(delta) > 1e-9) {
    const positiveIsGood = card.isHigher ? delta > 0 : delta < 0;
    const badgeColor = positiveIsGood
      ? 'text-emerald-700 bg-emerald-50 border-emerald-100'
      : 'text-rose-700 bg-rose-50 border-rose-100';
    const text = card.formatDelta ? card.formatDelta(delta) : delta.toFixed(2);
    deltaBadge = (
      <span
        className={clsx(
          'inline-block text-[9px] font-mono px-1 py-0.5 rounded border',
          badgeColor,
        )}
      >
        {text} 7d
      </span>
    );
  }
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-1">
      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400">
        {card.label}
      </div>
      <div className="text-base font-bold text-navy-900">{card.value}</div>
      {deltaBadge}
    </div>
  );
}

function FatigueSignal({ frequency }: { frequency: number }) {
  if (frequency < FATIGUE_WARNING_FREQUENCY) return null;
  const isCritical = frequency >= FATIGUE_CRITICAL_FREQUENCY;
  return (
    <div
      className={clsx(
        'flex items-center gap-2 text-xs rounded-lg border px-3 py-2',
        isCritical
          ? 'border-rose-200 bg-rose-50 text-rose-700'
          : 'border-amber-200 bg-amber-50 text-amber-700',
      )}
      data-testid="fatigue-signal"
      data-severity={isCritical ? 'critical' : 'warning'}
    >
      <AlertTriangle size={13} />
      <span>
        Frequency at <strong>{frequency.toFixed(1)}</strong> —{' '}
        {isCritical
          ? 'creative is likely fatigued. Consider pausing.'
          : 'approaching fatigue threshold.'}
      </span>
    </div>
  );
}

function DailyPerformanceChart({
  series,
}: {
  series: Array<{ date: string; spend?: number; roas?: number; ctr?: number }>;
}) {
  const [metric, setMetric] = useState<'spend' | 'roas' | 'ctr'>('spend');
  const dataKey = metric;
  const stroke = metric === 'roas' ? '#0d9488' : metric === 'ctr' ? '#3b82f6' : '#14b8a6';

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400">
          Daily Performance
        </p>
        <div className="flex gap-1">
          {(['spend', 'roas', 'ctr'] as const).map((opt) => (
            <button
              key={opt}
              onClick={() => setMetric(opt)}
              className={clsx(
                'text-[10px] font-mono uppercase px-2 py-0.5 rounded border transition-colors',
                metric === opt
                  ? 'bg-teal-50 border-teal-300 text-teal-700'
                  : 'bg-white border-slate-200 text-slate-500 hover:border-teal-200',
              )}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>
      <div className="h-32">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={series}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="date" stroke="#94a3b8" fontSize={9} />
            <YAxis stroke="#94a3b8" fontSize={9} />
            <Tooltip
              wrapperClassName="text-xs"
              contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0' }}
            />
            <Area
              type="monotone"
              dataKey={dataKey}
              stroke={stroke}
              fill={stroke}
              fillOpacity={0.25}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

function PersonaDeliveryTable({
  delivery,
}: {
  delivery: {
    on_target_visitors?: number;
    attributed_visitors?: number;
    personas?: Array<{
      persona_name: string;
      is_target?: boolean;
      visitors?: number;
      purchasers?: number;
      avg_ltv?: number;
    }>;
  };
}) {
  const personas = delivery.personas ?? [];
  const onTarget = delivery.on_target_visitors ?? 0;
  const attributed = delivery.attributed_visitors ?? 0;
  return (
    <section className="space-y-2">
      <h3 className="text-[10px] font-mono uppercase tracking-wider text-slate-400 flex items-center gap-1">
        <Users size={10} />
        Delivery by Persona
      </h3>
      {attributed > 0 && (
        <div className="text-[11px] text-slate-500 mb-1">
          {onTarget} of {attributed} attributed visitors landed on the targeted persona{' '}
          <span className="font-bold text-navy-900">
            ({Math.round((onTarget / attributed) * 100)}%)
          </span>
          .
        </div>
      )}
      <ul className="divide-y divide-slate-100 border border-slate-100 rounded-lg">
        {personas.map((p) => (
          <li
            key={p.persona_name}
            className={clsx(
              'flex items-center justify-between text-[11px] px-2 py-1.5',
              p.is_target && 'bg-emerald-50',
            )}
          >
            <span className="flex items-center gap-1 text-navy-900 truncate">
              {p.is_target && <Target size={10} className="text-emerald-600" />}
              {p.persona_name}
            </span>
            <span className="font-mono text-slate-500">
              {p.visitors ?? 0} visit · {p.purchasers ?? 0} buy · ${(p.avg_ltv ?? 0).toFixed(0)} LTV
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function GeneratedCreativePerformancePlaceholder() {
  return (
    <div className="rounded-xl border border-dashed border-slate-200 p-6 text-center space-y-2">
      <TrendingUp size={20} className="mx-auto text-slate-300" />
      <p className="text-sm font-medium text-slate-600">No live performance yet</p>
      <p className="text-xs text-slate-400">
        This creative has not been deployed to a paid platform. Performance data will populate once
        it goes live.
      </p>
    </div>
  );
}
